/* import __COLOCATED_TEMPLATE__ from './company-association-editor.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type CompanyApi from 'embercom/services/company-api';
import type { CompanyAttributes } from 'embercom/objects/inbox/company';
import CompanyAssociationPredicate from 'predicates/models/company-association';
import { postRequest } from 'embercom/lib/inbox/requests';
import type Session from 'embercom/services/session';
import Company from 'embercom/objects/inbox/company';

const PER_PAGE = 50;

interface Args {
  mode: 'default' | '???';
  predicate: CompanyAssociationPredicate;
  comparison: $TSFixMe;
  changePredicateValue: (value: $TSFixMe) => void;
  changePredicateComparison: (value: $TSFixMe) => void;
}

export default class CompanyAssociationEditor extends Component<Args> {
  @service declare session: Session;
  @service declare companyApi: CompanyApi;

  @tracked query = '';
  @tracked companies: Company[] = [];
  @tracked isLoadingCompanies = false;
  companiesAbortController: AbortController | null = null;
  pageNumber = 1;
  hasMore = true;

  comparisonOptions: $TSFixMe;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.comparisonOptions = this.args.predicate.getComparisonOptions({ mode: this.args.mode });
    this.loadCompanies();
  }

  willDestroy() {
    super.willDestroy();
    this.companiesAbortController?.abort();
    CompanyAssociationPredicate.justSelectedCompanies.delete(this);
  }

  get companyIdMap() {
    return new Map(this.companies.map((company) => [company.id, company]));
  }

  get selectedCompany() {
    return this.companyIdMap.get(this.args.predicate.value) ?? null;
  }

  @action changeValue(value: string) {
    let company = this.companyIdMap.get(value)!;
    CompanyAssociationPredicate.justSelectedCompanies.set(this, company);
    this.args.changePredicateValue(company.id);
  }

  @action changeCompany(value: Company) {
    this.changeValue(value.id);
  }

  @action changePredicateComparison(value: unknown) {
    this.args.changePredicateComparison(value);
  }

  @action changeSearchQuery(value: string) {
    if (this.query.trim() !== value.trim()) {
      this.query = value;
      this.loadCompanies();
    }
  }

  @action async loadMore() {
    if (this.hasMore) {
      await this.loadCompanies(this.pageNumber + 1);
    }
  }

  async loadCompanies(page = 1) {
    if (page !== this.pageNumber + 1 && page !== 1) {
      throw new Error('Page must follow current page or be 1');
    }

    this.isLoadingCompanies = true;

    if (this.companiesAbortController) {
      this.companiesAbortController.abort();
    }
    this.companiesAbortController = new AbortController();
    let { signal } = this.companiesAbortController;

    try {
      let selectedCompanyInExistingDataset =
        this.companyIdMap.get(this.args.predicate.value) ?? null;

      let [companiesResp, selectedCompany] = await Promise.all([
        this.#fetchCompanies({ searchByName: this.query, page }, { signal }),
        this.args.predicate.value && !selectedCompanyInExistingDataset
          ? this.companyApi.fetchCompany(this.args.predicate.value)
          : null,
      ]);
      signal.throwIfAborted();

      let companies = companiesResp.companies.map((attrs) => Company.deserialize(attrs));
      selectedCompany ??= selectedCompanyInExistingDataset;
      if (
        page === 1 &&
        selectedCompany &&
        !new Set(companies.map(({ id }) => id)).has(selectedCompany.id)
      ) {
        companies.unshift(selectedCompany);
      }

      this.companies = page === 1 ? companies : [...this.companies, ...companies];
      this.pageNumber = companiesResp.page;
      this.hasMore = companiesResp.next_page !== null;

      // Auto-select the first company if no value is set and we have companies
      if (!this.args.predicate.value && companies.length > 0) {
        this.changeCompany(companies[0]);
      }
    } finally {
      this.isLoadingCompanies = false;
    }
  }

  async #fetchCompanies(
    options: { searchByName?: string; page?: number },
    { signal }: { signal?: AbortSignal } = {},
  ) {
    let predicates = [];
    if (options.searchByName) {
      predicates.push({
        attribute: 'company.name',
        comparison: 'contains',
        type: 'string',
        value: options.searchByName,
      });
    }

    let response = await postRequest(
      `/ember/companies/search.json`,
      {
        app_id: this.session.workspace.id,
        predicates,
        page: options.page ?? 1,
        per_page: PER_PAGE,
        sort_by: 'name',
        sort_direction: 'asc',
        include_count: true,
      },
      { signal },
    );

    let responseData = (await response.json()) as {
      companies: CompanyAttributes[];
      page: number;
      per_page: number;
      total_count: number;
      total_pages: number;
      next_page: number | null;
      previous_page: number | null;
    };

    return responseData;
  }
}
